import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import useOllieFindingAction from 'src/hooks/useOllieFindingAction'
import {
  OLLIE_ACTION_COPY,
  OLLIE_SHEET_KINDS,
  OLLIE_WEEKDAYS,
  buildOllieBusinessDaysConfirm,
  buildOllieParRows,
  buildOllieParsConfirm,
  buildOllieStockTakeConfirm,
  buildOllieStockTakeRows,
  isValidOlliePar,
  ollieParsButtonLabel,
  ollieRecipeHandoffPath,
  ollieSheetKind,
  toggleOllieWeekday,
} from 'src/utils/ollieActionFlows'
import OllieSupplierDrafts from './OllieSupplierDrafts'

/**
 * Prepared work for one finding action, shown in the chat: review, edit,
 * then one confirm button. Express saves only after that press; drafts are
 * never sent by OMS.
 */
const OllieActionSheet = ({
  checkId,
  action,
  venueId,
  onClose,
  onNavigate,
  onShowPaywall,
}) => {
  const finding = useMemo(() => ({ checkId }), [checkId])
  const { status, prepared, result, error, prepare, confirm, draftUsed } =
    useOllieFindingAction(finding)
  const [invalid, setInvalid] = useState(null)
  const [parRows, setParRows] = useState([])
  const [stockRows, setStockRows] = useState([])
  const [period, setPeriod] = useState({ startDate: '', endDate: '' })
  const [weekdays, setWeekdays] = useState([])

  const preparedForRef = useRef(null)
  useEffect(() => {
    if (preparedForRef.current === action.id) return
    preparedForRef.current = action.id
    prepare(action.id)
  }, [prepare, action.id])

  useEffect(() => {
    if (!prepared) return
    setParRows(buildOllieParRows(prepared))
    setStockRows(buildOllieStockTakeRows(prepared))
    setPeriod({
      startDate: prepared.period?.startDate || '',
      endDate: prepared.period?.endDate || '',
    })
    setWeekdays(Array.isArray(prepared.weekdays) ? prepared.weekdays : [])
  }, [prepared])

  const kind = ollieSheetKind(prepared)
  const busy = status === 'preparing' || status === 'confirming'
  const keptPars = parRows.filter(row => row.keep).length

  const submit = async built => {
    if (!built.ok) {
      setInvalid(built.message)
      return
    }
    setInvalid(null)
    await confirm(action.id, { items: built.items, fields: built.fields })
  }

  const handleConfirm = () => {
    if (kind === OLLIE_SHEET_KINDS.PARS) submit(buildOllieParsConfirm(parRows))
    if (kind === OLLIE_SHEET_KINDS.STOCK_TAKE) {
      submit(buildOllieStockTakeConfirm({ ...period, rows: stockRows }))
    }
    if (kind === OLLIE_SHEET_KINDS.BUSINESS_DAYS) {
      submit(buildOllieBusinessDaysConfirm(weekdays))
    }
  }

  const updateParRow = (productId, patch) =>
    setParRows(rows =>
      rows.map(row =>
        row.productId === productId ? { ...row, ...patch } : row,
      ),
    )

  const toggleStockRow = productId =>
    setStockRows(rows =>
      rows.map(row =>
        row.productId === productId ? { ...row, keep: !row.keep } : row,
      ),
    )

  if (status === 'preparing' && !prepared) {
    return (
      <Sheet aria-busy='true'>
        <Muted>{OLLIE_ACTION_COPY.preparing}</Muted>
      </Sheet>
    )
  }

  if (!prepared) {
    return (
      <Sheet role='alert'>
        <Body>{error?.message || 'Ollie could not get this ready.'}</Body>
        <Buttons>
          <QuietButton type='button' onClick={onClose}>
            {OLLIE_ACTION_COPY.close}
          </QuietButton>
        </Buttons>
      </Sheet>
    )
  }

  if (result) {
    const stockTakeId = result.stockTake?._id
    return (
      <Sheet role='status'>
        <Body>{result.message}</Body>
        {result.limitReached?.message && (
          <Muted>{result.limitReached.message}</Muted>
        )}
        <Buttons>
          {stockTakeId && (
            <PrimaryButton
              type='button'
              onClick={() =>
                onNavigate(`/v/${venueId}/stockTakes/${stockTakeId}`)
              }
            >
              Open stock take
            </PrimaryButton>
          )}
          {result.limitReached && onShowPaywall && (
            <QuietButton type='button' onClick={onShowPaywall}>
              {OLLIE_ACTION_COPY.upgrade}
            </QuietButton>
          )}
          <QuietButton type='button' onClick={onClose}>
            {OLLIE_ACTION_COPY.close}
          </QuietButton>
        </Buttons>
      </Sheet>
    )
  }

  if (kind === OLLIE_SHEET_KINDS.DRAFTS) {
    return (
      <Sheet>
        <OllieSupplierDrafts
          prepared={prepared}
          onUsed={(via, supplierId) => draftUsed(action.id, via, supplierId)}
        />
        <Buttons>
          <QuietButton type='button' onClick={onClose}>
            {OLLIE_ACTION_COPY.close}
          </QuietButton>
        </Buttons>
      </Sheet>
    )
  }

  if (kind === OLLIE_SHEET_KINDS.NOTHING) {
    return (
      <Sheet role='status'>
        <Body>{prepared.message || 'Nothing to do here right now.'}</Body>
        <Buttons>
          <QuietButton type='button' onClick={onClose}>
            {OLLIE_ACTION_COPY.close}
          </QuietButton>
        </Buttons>
      </Sheet>
    )
  }

  const { confirm: copy } = prepared

  if (kind === OLLIE_SHEET_KINDS.RECIPE_DRAFT) {
    const dish = prepared.dishes[0]
    const path = ollieRecipeHandoffPath(venueId, dish)
    return (
      <Sheet>
        <Title>{copy.title}</Title>
        <Body>{copy.body}</Body>
        {dish?.basisLabel && <Label>{dish.basisLabel}</Label>}
        <List>
          {(dish?.ingredients || []).map(item => (
            <Row key={item.productId}>
              <span>{item.title}</span>
              {item.UoM && <Muted as='span'>{item.UoM}</Muted>}
            </Row>
          ))}
        </List>
        {prepared.message && <Muted>{prepared.message}</Muted>}
        <Buttons>
          {path && (
            <PrimaryButton type='button' onClick={() => onNavigate(path)}>
              {copy.button}
            </PrimaryButton>
          )}
          <QuietButton type='button' onClick={onClose}>
            {OLLIE_ACTION_COPY.cancel}
          </QuietButton>
        </Buttons>
      </Sheet>
    )
  }

  const buttonLabel =
    kind === OLLIE_SHEET_KINDS.PARS
      ? ollieParsButtonLabel(keptPars)
      : copy.button

  return (
    <Sheet>
      <Title>{copy.title}</Title>
      <Body>{copy.body}</Body>
      {copy.freeNote && <Muted>{copy.freeNote}</Muted>}

      {kind === OLLIE_SHEET_KINDS.PARS && (
        <List>
          {parRows.map(row => (
            <Row key={row.productId}>
              <CheckLabel>
                <input
                  type='checkbox'
                  checked={row.keep}
                  disabled={busy}
                  onChange={() =>
                    updateParRow(row.productId, { keep: !row.keep })
                  }
                />
                <span>{row.title}</span>
              </CheckLabel>
              <ParInput
                type='number'
                min={0}
                max={999}
                step='any'
                aria-label={`Par for ${row.title}`}
                value={row.value}
                disabled={busy || !row.keep}
                $invalid={row.keep && !isValidOlliePar(row.value)}
                onChange={event =>
                  updateParRow(row.productId, { value: event.target.value })
                }
              />
              {row.UoM && <Muted as='span'>{row.UoM}</Muted>}
            </Row>
          ))}
        </List>
      )}

      {kind === OLLIE_SHEET_KINDS.STOCK_TAKE && (
        <>
          <Label>{OLLIE_ACTION_COPY.period}</Label>
          <DateRow>
            <label>
              {OLLIE_ACTION_COPY.from}{' '}
              <DateInput
                type='date'
                value={period.startDate}
                disabled={busy}
                onChange={event =>
                  setPeriod(p => ({ ...p, startDate: event.target.value }))
                }
              />
            </label>
            <label>
              {OLLIE_ACTION_COPY.until}{' '}
              <DateInput
                type='date'
                value={period.endDate}
                disabled={busy}
                onChange={event =>
                  setPeriod(p => ({ ...p, endDate: event.target.value }))
                }
              />
            </label>
          </DateRow>
          {stockRows.length > 0 && (
            <>
              <Label>{OLLIE_ACTION_COPY.products}</Label>
              <List>
                {stockRows.map(row => (
                  <Row key={row.productId}>
                    <CheckLabel>
                      <input
                        type='checkbox'
                        checked={row.keep}
                        disabled={busy}
                        onChange={() => toggleStockRow(row.productId)}
                      />
                      <span>{row.title}</span>
                    </CheckLabel>
                  </Row>
                ))}
              </List>
            </>
          )}
        </>
      )}

      {kind === OLLIE_SHEET_KINDS.BUSINESS_DAYS && (
        <DayRow role='group' aria-label='Ordering days'>
          {OLLIE_WEEKDAYS.map(({ day, label }) => (
            <DayChip
              key={day}
              type='button'
              aria-pressed={weekdays.includes(day)}
              $on={weekdays.includes(day)}
              disabled={busy}
              onClick={() => setWeekdays(list => toggleOllieWeekday(list, day))}
            >
              {label}
            </DayChip>
          ))}
        </DayRow>
      )}

      {(invalid || (status === 'error' && error?.message)) && (
        <ErrorText role='alert'>{invalid || error.message}</ErrorText>
      )}

      <Buttons>
        <PrimaryButton
          type='button'
          disabled={busy || (kind === OLLIE_SHEET_KINDS.PARS && !keptPars)}
          onClick={handleConfirm}
        >
          {status === 'confirming' ? OLLIE_ACTION_COPY.confirming : buttonLabel}
        </PrimaryButton>
        <QuietButton type='button' disabled={busy} onClick={onClose}>
          {OLLIE_ACTION_COPY.cancel}
        </QuietButton>
      </Buttons>
    </Sheet>
  )
}

export const Sheet = styled.div`
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-left: 3px solid ${({ theme }) => theme.colours.lettuceGreen};
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 14px;
`

const Title = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Body = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Muted = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const Label = styled.p`
  margin: 12px 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
  max-height: 260px;
  overflow-y: auto;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
`

const ParInput = styled.input`
  width: 72px;
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $invalid }) =>
      $invalid ? theme.colours.chilliRed : theme.colours.panSilver};
  font-size: 14px;
`

const DateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const DateInput = styled.input`
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  font-size: 13px;
`

const DayRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`

const DayChip = styled.button`
  border: 1px solid
    ${({ theme, $on }) =>
      $on ? theme.colours.lettuceGreen : theme.colours.panSilver};
  background: ${({ theme, $on }) =>
    $on ? theme.colours.lettuceGreen : theme.colours.mayoWhite};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`

const ErrorText = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.chilliRed};
`

export const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
`

export const PrimaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

export const QuietButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  background: ${({ theme }) => theme.colours.mayoWhite};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

export default OllieActionSheet
